import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Card,
  Descriptions,
  Empty,
  message,
  Spin,
  Table,
  Tag,
  Tabs,
  Tooltip,
} from "antd";
import {
  ArrowLeftOutlined,
  DeleteOutlined,
  NotificationOutlined,
} from "@ant-design/icons";
import { RxRocket } from "react-icons/rx";
import CustomBreadcrumb from "../../../components/breadcrumb/Breadcrumb";
import {
  convertActivationActionToCampaign,
  deleteActivationAction,
  getActivationAction,
  getActivationActionClients,
} from "../../../api/Services";
import { hasPermission, privilegesEnum } from "../../../helpers/PermissionHelper";
import { formatRecommendationMessage } from "../../../helpers/RecommendationMessageHelper";
import styles from "./ViewActivationAction.module.scss";
import NotFound from "../../notfound/NotFound";

const { TabPane } = Tabs;

const ViewActivationAction = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState<any | null>(null);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [clients, setClients] = useState<any[]>([]);
  const [clientsTotal, setClientsTotal] = useState(0);
  const [clientsPage, setClientsPage] = useState(1);
  const [clientsPageSize, setClientsPageSize] = useState(20);

  const actionId = useMemo(() => {
    const parsed = Number(id);
    return Number.isFinite(parsed) ? parsed : null;
  }, [id]);

  const getBackUrl = useCallback(() => {
    const searchParams = new URLSearchParams(location.search);
    const queryString = searchParams.toString();
    return `/commercial-intelligence/opportunities/activation-actions${
      queryString ? `?${queryString}` : ""
    }`;
  }, [location.search]);

  const fetchAction = useCallback(async () => {
    if (!actionId) {
      setLoading(false);
      setAction(null);
      return;
    }
    setLoading(true);
    const response = await getActivationAction(actionId);
    setAction(response);
    setLoading(false);
  }, [actionId]);

  useEffect(() => {
    fetchAction();
  }, [fetchAction]);

  useEffect(() => {
    if (!actionId) return;
    setClientsPage(1);
    fetchClients(1, clientsPageSize);
    // fetchClients depends on clientsPage; avoid triggering extra loads
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionId]);

  const fetchClients = useCallback(
    async (page = clientsPage, size = clientsPageSize) => {
      if (!actionId) return;
      setClientsLoading(true);
      const response = await getActivationActionClients(actionId, page, size);
      setClients(response.clients || []);
      setClientsTotal(response.totalElements || 0);
      setClientsLoading(false);
    },
    [actionId, clientsPage, clientsPageSize]
  );

  const statusTag = (status?: string) => {
    if (!status) return null;
    return (
      <Tag
        className={styles.tag}
        color={status === "APPROVED" ? "green" : "blue"}
      >
        {status === "APPROVED"
          ? t("ActivationAction-Status-Approved")
          : t("ActivationAction-Status-Suggested")}
      </Tag>
    );
  };

  const handleConvertToCampaign = async () => {
    if (!actionId) return;
    const result = await convertActivationActionToCampaign(actionId);
    if (result.success && result.data?.campaignId) {
      message.success(t("ActivationAction-ConvertSuccess"));
      navigate(`/commercial-intelligence/campaigns/${result.data.campaignId}/view`);
      return;
    }
    if (result.timedOut) {
      message.warning(
        t(
          "ActivationAction-ConvertTimeout",
          "La conversión está tardando. Refrescá la página: si aparece Aprobada, la campaña ya se creó.",
        ),
      );
      await fetchAction();
      return;
    }
    message.error(result.message || t("Common-ErrorMessage"));
    await fetchAction();
  };

  const handleDelete = async () => {
    if (!actionId) return;
    const ok = await deleteActivationAction(actionId);
    if (!ok) {
      message.error(t("Common-DeleteErrorMessage"));
      return;
    }
    message.success(t("Common-DeleteSuccessMessage"));
    navigate(getBackUrl());
  };

  const articles = Array.isArray(action?.articles) ? action.articles : [];

  const clientColumns = [
    {
      title: t("Common-Identifier"),
      key: "identifier",
      render: (_: any, record: any) => {
        const externalId = record.externalId ?? record.external_id;
        const idNumber = record.idNumber;
        const main =
          (idNumber != null && String(idNumber).trim() !== ""
            ? String(idNumber)
            : null) ||
          (externalId != null && String(externalId).trim() !== ""
            ? String(externalId)
            : null) ||
          "-";
        return hasPermission(privilegesEnum.READ_CLIENTS) ? (
          <a href={`/commercial-intelligence/clients/${record.id}/view`}>{main}</a>
        ) : (
          main
        );
      },
    },
    {
      title: t("Common-Name"),
      key: "name",
      render: (_: any, record: any) => record.name || "-",
    },
    {
      title: t("Common-Email"),
      dataIndex: "email",
      key: "email",
      render: (v: any) => v || "-",
    },
    {
      title: t("Common-Phone"),
      dataIndex: "phone",
      key: "phone",
      render: (v: any) => v || "-",
    },
  ];

  const articleColumns = [
    {
      title: t("Common-Id"),
      dataIndex: "id",
      key: "id",
      width: 90,
    },
    {
      title: t("Common-Name"),
      dataIndex: "name",
      key: "name",
      render: (name: string, record: any) =>
        record?.id ? (
          <a href={`/admin/articles/${record.id}/view`}>{name || "-"}</a>
        ) : (
          name || "-"
        ),
    },
  ];

  if (!loading && !action) {
    return <NotFound />;
  }

  return (
    <div className={styles.wrapper}>
      <CustomBreadcrumb
        items={[
          {
            icon: <RxRocket />,
            label: t("ActivationAction-Title"),
            path: "/commercial-intelligence/opportunities/activation-actions",
            search: location.search,
          },
          {
            label: t("ActivationAction-DetailTitle"),
            isCurrentPage: true,
          },
        ]}
      />

      <div className={styles.headerRow}>
        <div className={styles.title}>
          <div className={styles.titleText}>
            {formatRecommendationMessage(
              t,
              action?.nameKey,
              action?.nameParams,
              action?.name
            ) || t("ActivationAction-DetailTitle")}
            {statusTag(action?.status)}
          </div>
        </div>
        <div>
          <Tooltip title={t("Common-Back")}>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate(getBackUrl())}
            >
              {t("Common-Back")}
            </Button>
          </Tooltip>
          <Tooltip title={t("ActivationAction-ConvertToCampaign")}>
            <Button
              style={{ marginLeft: 8 }}
              icon={<NotificationOutlined />}
              onClick={handleConvertToCampaign}
              disabled={action?.campaignId != null}
            >
              {t("ActivationAction-ConvertToCampaign")}
            </Button>
          </Tooltip>
          {hasPermission(privilegesEnum.DELETE_ACTIVATION_ACTIONS) &&
            action?.campaignId == null && (
              <Tooltip title={t("Common-Delete")}>
                <Button
                  danger
                  style={{ marginLeft: 8 }}
                  icon={<DeleteOutlined />}
                  onClick={handleDelete}
                >
                  {t("Common-Delete")}
                </Button>
              </Tooltip>
            )}
        </div>
      </div>

      <Spin spinning={loading}>
        <Tabs className={styles.tabs} defaultActiveKey="summary">
          <TabPane tab={t("ActivationAction-TabSummary")} key="summary">
            <Card>
              <Descriptions bordered size="small" column={2}>
                <Descriptions.Item label={t("Common-Name")}>
                  {formatRecommendationMessage(
                    t,
                    action?.nameKey,
                    action?.nameParams,
                    action?.name
                  ) || "-"}
                </Descriptions.Item>
                <Descriptions.Item label={t("Common-Status")}>
                  {action?.status || "-"}
                </Descriptions.Item>
                <Descriptions.Item label={t("ActivationAction-RuleType")}>
                  {action?.ruleType || "-"}
                </Descriptions.Item>
                <Descriptions.Item label={t("ActivationAction-Audience")}>
                  {action?.audienceCount ?? "-"}
                </Descriptions.Item>
                <Descriptions.Item label={t("ActivationAction-EstimatedConversion")}>
                  {action?.estimatedConversion ?? "-"}
                </Descriptions.Item>
                <Descriptions.Item label={t("Common-EstimatedImpact")}>
                  {action?.estimatedImpact != null ? `$${action.estimatedImpact}` : "-"}
                </Descriptions.Item>
                <Descriptions.Item label={t("ActivationAction-SourceSegment")}>
                  {action?.sourceSegmentId ? (
                    <a
                      href={`/commercial-intelligence/segments/${action.sourceSegmentId}/view`}
                    >
                      {formatRecommendationMessage(
                        t,
                        action?.sourceSegmentNameKey,
                        action?.sourceSegmentNameParams,
                        action?.sourceSegmentName
                      ) || action.sourceSegmentId}
                    </a>
                  ) : (
                    formatRecommendationMessage(
                      t,
                      action?.sourceSegmentNameKey,
                      action?.sourceSegmentNameParams,
                      action?.sourceSegmentName
                    ) || "-"
                  )}
                </Descriptions.Item>
                <Descriptions.Item label={t("ActivationAction-Campaign")}>
                  {action?.campaignId ? (
                    <a
                      href={`/commercial-intelligence/campaigns/${action.campaignId}/view`}
                    >
                      {action.campaignId}
                    </a>
                  ) : (
                    "-"
                  )}
                </Descriptions.Item>
                <Descriptions.Item label={t("Common-RegisterDate")}>
                  {action?.createdAt ? String(action.createdAt).split("T")[0] : "-"}
                </Descriptions.Item>
                <Descriptions.Item label={t("Common-Id")}>
                  {action?.id ?? "-"}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </TabPane>

          <TabPane tab={t("ActivationAction-TabArticles")} key="articles">
            <Card>
              {articles.length === 0 ? (
                <Empty description={t("ActivationAction-NoArticles")} />
              ) : (
                <Table
                  rowKey="id"
                  dataSource={articles}
                  columns={articleColumns}
                  pagination={{ pageSize: 10 }}
                />
              )}
            </Card>
          </TabPane>

          <TabPane tab={t("ActivationAction-TabClients")} key="clients">
            <Card>
              <Table
                rowKey="id"
                loading={clientsLoading}
                dataSource={clients}
                columns={clientColumns as any}
                pagination={{
                  current: clientsPage,
                  pageSize: clientsPageSize,
                  total: clientsTotal,
                  showSizeChanger: true,
                  onChange: (page, pageSize) => {
                    setClientsPage(page);
                    setClientsPageSize(pageSize);
                    fetchClients(page, pageSize);
                  },
                }}
                locale={{
                  emptyText: (
                    <Empty description={t("ActivationAction-NoClients")} />
                  ),
                }}
                onChange={() => {
                  // noop (keeps antd happy when sorting is added later)
                }}
              />
            </Card>
          </TabPane>
        </Tabs>
      </Spin>
    </div>
  );
};

export default ViewActivationAction;

